/** 
 * © 2025 COMPUTACIÓN EN ACCIÓN, S.A. DE C.V. 'CONTPAQi'. Todos los derechos reservados. 
 * Este código fuente constituye una obra original protegida por la legislación nacional e internacional en materia de propiedad intelectual. 
 * Su reproducción, distribución, comunicación pública, transformación, descompilación, ingeniería inversa o cualquier uso no autorizado, 
 * total o parcial, se encuentra expresamente prohibido sin la previa autorización por escrito de COMPUTACIÓN EN ACCIÓN, S.A. DE C.V. 
 * El uso indebido de este software puede ser constitutivo de delitos y dará lugar a las responsabilidades civiles y penales correspondientes. 
*/ 
 
import {put, takeEvery, select, call } from '@redux-saga/core/effects';
import posthog from 'posthog-js';
import {
  setSettingsSuccess,
  setSettingsFailure,
  updateSettingsSuccess,
  updateSettingsFailure,
  getSettingsSuccess,
  getSettingsFailure
} from '../actions/settings.action';
import {
  SET_SETTINGS,
  SAVE_SETTINGS,
  GET_SETTINGS,
  SetSettings,
  GetSettings,
} from 'src/types/settings.type';
import { getPrettierMessage } from 'src/utils/global';
import { ApiErrorsCodes, ApiRbacPaths } from 'src/constants/endpoints.constant';
import { ApiRbacUrl } from 'src/utils/config';
import { parseResponse, requestApi } from 'src/providers/http-api.provider';
import { Messages } from 'src/constants/message.constant';
import Role from 'src/models/role.model';
import { SettingsViewType } from 'src/constants/settings.contant';
import { deleteRolesSuccess, getRoles, updateRolOnList } from '../actions/roles.action';
import { isRoleUpdated } from 'src/utils/permissions';

function* onGetSettings(data:GetSettings):any {
  try {
    const platformToken = yield select(state => state.config?.platformToken);
    const serviceId =  yield select(state => state.config?.serviceId);
    const licenseId = yield select(state => state.config?.licenseId);
    const roleId = data?.payload?.roleId;
    const mode = data?.payload?.mode;
    if (platformToken && licenseId && roleId && serviceId) {
      const endpoint = `${ApiRbacUrl}${ApiRbacPaths.BASE}/${serviceId}/licenses/${licenseId}/roles/${roleId}`;
      let response = yield call(requestApi, {
        endpoint,
        method: 'GET',
        headers: {
          Authorization: platformToken,
        },
        useParseResponse: false,
      });
      if (response?.ok) {
        let rolesData = yield call(parseResponse, response);
        const listRoleModel = new Role();
        const roles = listRoleModel.getListRoleModel(rolesData);
        if (roles?.length > 0) {
          let payloadRol = {
            role: roles[0],
            mode,
          }
          yield put(updateRolOnList({role: payloadRol.role, serviceId, licenseId}));
          yield put(getSettingsSuccess(payloadRol));
        } else {
          throw new Error(Messages.ROL.SETTINGS_GET_EMPTY);
        }
      } else {
        const apiError = yield call(parseResponse, response);
        let errorMessage = apiError?.detail;
        if (response?.status === ApiErrorsCodes.NOT_FOUND) {
          const currentRoles = yield select(state => state.roles?.data);
          let updatedRoles = currentRoles.filter((rol:any) => rol.roleId !== roleId);
          let payloadRoles = {
            roles: updatedRoles,
            licenseId,
            serviceId,
          }
          errorMessage = Messages.ROL.SETTINGS_GET_EMPTY;
          yield put(deleteRolesSuccess(payloadRoles));
        }
        throw new Error(getPrettierMessage(errorMessage));
      }
    } else {
      throw new Error(Messages.PARAMS_EMPTY);
    }
  } catch (err:any) {
    console.log("Fail onGetSettings: ", err);
    yield put(getSettingsFailure(err?.message || Messages.ROL.SETTINGS_GET_ERROR));
  }
}

function* onSetSettings(data:SetSettings) {
  try {
    let currentData = {
      ...data?.payload,
    }
    yield put(setSettingsSuccess(currentData));
  } catch (err) {
    yield put(setSettingsFailure(err));
  }
}

function* onSaveSettings(data:GetSettings):any {
  try {
    const platformToken = yield select(state => state.config?.platformToken);
    const serviceId =  yield select(state => state.config?.serviceId);
    const licenseId = yield select(state => state.config?.licenseId);
    const userId = yield select(state => state.config?.identityId);
    const mode = yield select(state => state.settings?.mode);
    const roles = yield select(state => state.roles);
    let roleInfo = {...yield select(state => state.settings.role)};
    const isUpdatedData = isRoleUpdated(roles.all, roleInfo, serviceId, licenseId);

    const canSave = mode === SettingsViewType.UPDATE || mode === SettingsViewType.CREATION;
    const isUpdate = mode === SettingsViewType.UPDATE;
    const roleId = isUpdate ? data?.payload?.roleId : undefined;
    const isValidRoleId = isUpdate ? roleId : true;
    delete roleInfo['roleId'];
    delete roleInfo['canDelete'];
    delete roleInfo['canEdit'];

    if (canSave && platformToken && licenseId && serviceId && isValidRoleId) {
      let hasDataUpdated = mode === SettingsViewType.CREATION || (isUpdate && isUpdatedData);
      if (hasDataUpdated) {
        let endpoint = `${ApiRbacUrl}${ApiRbacPaths.BASE}/${serviceId}/licenses/${licenseId}/roles`;
        if (roleId && isUpdate) {
          endpoint = `${endpoint}/${roleId}`;
        }
        let response = yield call(requestApi, {
          endpoint,
          method: isUpdate ? 'PUT' : 'POST',
          headers: {
            Authorization: platformToken,
            'Content-Type': 'application/json',
          },
          body: roleInfo,
          useParseResponse: false,
        });
        if (response?.ok) {
          yield put(updateSettingsSuccess());
          yield put(getRoles({serviceId}));
          if (posthog) {
            let responseData = yield call(parseResponse, response);
            let rolePostHogData = {
              roleId: responseData?.roleId,
              roleName: roleInfo?.roleName,
              serviceId,
              modifiedBy: userId,
              licenseId,
            };
            posthog?.capture(roleId && isUpdate ? 'role_updated' : 'role_created', rolePostHogData);
          }
        } else {
          const apiError = yield call(parseResponse, response);
          throw new Error(getPrettierMessage(apiError?.detail));
        }
      } else {
        yield put(updateSettingsSuccess());
      }
    } else {
      throw new Error(Messages.PARAMS_EMPTY);
    }
  } catch (err:any) {
    console.log("onSaveSettings: ", err);
    yield put(updateSettingsFailure(err?.message || Messages.ROL.SETTINGS_SAVE_ERROR));
  }
}

export function* watchOnSetSettings() {
  yield takeEvery(SET_SETTINGS, onSetSettings);
}

export function* watchOnSaveSettings() {
  yield takeEvery(SAVE_SETTINGS, onSaveSettings);
}

export function* watchOnGetSettings() {
  yield takeEvery(GET_SETTINGS, onGetSettings);
}
